import type { FilterFieldKey, TaskFilter } from '@/lib/types/filter'

/**
 * OR groups (design 9b): "my tasks in this cycle, or anything overdue".
 * Filters with a `group` are ORed group-against-group and ANDed within a
 * group; filters without one (pinned ones, and every filter while there's
 * only one group) apply on top of all groups. One level only — no nesting.
 */

let seq = 0
export function newFilterId(): string {
  seq += 1
  return `f${Date.now().toString(36)}${seq.toString(36)}`
}

/** Give every filter a chip id (saved views and old local state have none). */
export function withIds(filters: ReadonlyArray<TaskFilter>): Array<TaskFilter> {
  return filters.map((f) => (f.id ? f : { ...f, id: newFilterId() }))
}

export function isGrouped(filters: ReadonlyArray<TaskFilter>): boolean {
  return filters.some((f) => f.group !== undefined)
}

/** Group numbers in use, ascending. */
export function groupNumbers(filters: ReadonlyArray<TaskFilter>): Array<number> {
  return [...new Set(filters.flatMap((f) => (f.group === undefined ? [] : [f.group])))].sort(
    (a, b) => a - b,
  )
}

/**
 * Keep groups canonical: numbered 0..n-1, and a lone group dissolves back
 * into plain filters so a single-group view saves in the pre-groups shape.
 */
export function normalizeGroups(filters: ReadonlyArray<TaskFilter>): Array<TaskFilter> {
  const groups = groupNumbers(filters)
  if (groups.length === 0) return [...filters]
  if (groups.length === 1) {
    return filters.map((f) => (f.group === undefined ? f : { ...f, group: undefined }))
  }
  const renumber = new Map(groups.map((g, i) => [g, i]))
  return filters.map((f) => (f.group === undefined ? f : { ...f, group: renumber.get(f.group) }))
}

/**
 * Add a filter as the first member of a new OR group. Going from one group to
 * two moves the existing (unlocked) filters into group 0 first; locked ones
 * (pinned project on a space) stay applied to every group.
 */
export function addToNewGroup(
  filters: ReadonlyArray<TaskFilter>,
  filter: TaskFilter,
  lockedFields: ReadonlyArray<FilterFieldKey> = [],
): Array<TaskFilter> {
  const locked = new Set(lockedFields)
  const base = isGrouped(filters)
    ? [...filters]
    : filters.map((f) => (locked.has(f.fieldKey) ? f : { ...f, group: 0 }))
  const groups = groupNumbers(base)
  const next = groups.length === 0 ? 0 : Math.max(...groups) + 1
  return normalizeGroups([...base, { ...filter, id: filter.id ?? newFilterId(), group: next }])
}

/**
 * Put filters in place: each replaces the filter on the same property in the
 * same group, the rest are appended (quick filters, typed filters).
 */
export function upsertFilters(
  filters: ReadonlyArray<TaskFilter>,
  incoming: ReadonlyArray<TaskFilter>,
): Array<TaskFilter> {
  const result = [...filters]
  for (const f of incoming) {
    const i = result.findIndex((x) => x.fieldKey === f.fieldKey && x.group === f.group)
    const next = { ...f, id: i >= 0 ? result[i]?.id : (f.id ?? newFilterId()) }
    if (i >= 0) result[i] = next
    else result.push(next)
  }
  return normalizeGroups(result)
}

/** Swap everything the user can change for `incoming`, keeping locked filters. */
export function replaceUnlockedFilters(
  filters: ReadonlyArray<TaskFilter>,
  incoming: ReadonlyArray<TaskFilter>,
  lockedFields: ReadonlyArray<FilterFieldKey> = [],
): Array<TaskFilter> {
  const locked = new Set(lockedFields)
  const kept = filters.filter((f) => locked.has(f.fieldKey) && f.group === undefined)
  return normalizeGroups([...kept, ...withIds(incoming.filter((f) => !locked.has(f.fieldKey)))])
}
